import {
    useMutation,
    UseMutationResult,
    useQueryClient,
} from '@tanstack/react-query';
import { useAuthenticatedQuery } from 'flow/hooks/api/useAuthenticatedQuery';
import { useMemo } from 'react';
import PremadeAnswersResource from 'flow/api/premadeanswer';
import { PremadeAnswer, PremadeAnswerType } from 'flow/types/Chats';

const queryKey = ['premadeAnswers'];

export const useCreatePremadeAnswer = (): UseMutationResult<
    PremadeAnswer,
    Error,
    {
        name: string;
        message: string;
        isDefault?: boolean;
        type?: PremadeAnswerType;
    }
> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: queryKey,
        mutationFn: ({ name, message, isDefault, type }) =>
            new Promise((resolve, reject) => {
                PremadeAnswersResource.createPremadeAnswer(
                    {
                        onResult: resolve,
                        onError: reject,
                        onClientError: reject,
                    },
                    {
                        name,
                        message,
                        isDefault,
                        type,
                    },
                );
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
};

export const useDeletePremadeAnswer = (): UseMutationResult<
    PremadeAnswer,
    Error,
    { key: string }
> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: queryKey,
        mutationFn: ({ key }) =>
            new Promise((resolve, reject) => {
                PremadeAnswersResource.deletePremadeAnswer(
                    {
                        onResult: resolve,
                        onError: reject,
                        onClientError: reject,
                    },
                    key,
                );
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
};

export const usePremadeAnswers = () =>
    useAuthenticatedQuery({
        queryKey,
        queryFn: () =>
            new Promise<PremadeAnswer[]>((resolve, reject) => {
                PremadeAnswersResource.getPremadeAnswers({
                    onResult: resolve,
                    onError: reject,
                    onClientError: reject,
                });
            }),
    });

export const useUpdatePremadeAnswer = (): UseMutationResult<
    PremadeAnswer,
    Error,
    {
        key: string;
        name: string;
        message: string;
        isDefault?: boolean;
        type?: PremadeAnswerType;
    }
> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: queryKey,
        mutationFn: ({ key, name, message, isDefault, type }) =>
            new Promise((resolve, reject) => {
                PremadeAnswersResource.updatePremadeAnswer(
                    {
                        onResult: resolve,
                        onError: reject,
                        onClientError: reject,
                    },
                    key,
                    {
                        key,
                        name,
                        message,
                        isDefault,
                        type,
                    },
                );
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
};

export const useDefaultPremadeAnswer = () => {
    const { data: premadeAnswers } = usePremadeAnswers();
    return useMemo(
        () =>
            premadeAnswers?.find(
                (pa) => pa.isDefault && pa.type === 'EMAIL_SIGNATURE',
            ),
        [premadeAnswers],
    );
};
